// Export the generated week schedule to CSV and printable per-day PDF sheets.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { slotToLabel, shiftHours } from "./time";

// Collapse per-slot assignments into contiguous (state, workstation) segments.
function buildSegments(schedule, config) {
  const byAD = {};
  (schedule?.assignments || []).forEach((a) => {
    (byAD[`${a.agent_id}|${a.day}`] ||= []).push(a);
  });
  const segs = [];
  Object.entries(byAD).forEach(([k, list]) => {
    const [aid, day] = k.split("|");
    list.sort((x, y) => x.slot - y.slot);
    let cur = null;
    list.forEach((a) => {
      const key = `${a.state}|${a.workstation_id || ""}`;
      if (cur && a.slot === cur.endSlot && cur.key === key) cur.endSlot = a.slot + 1;
      else {
        if (cur) segs.push(cur);
        cur = { agentId: aid, day: Number(day), startSlot: a.slot, endSlot: a.slot + 1, state: a.state, ws: a.workstation_id, key };
      }
    });
    if (cur) segs.push(cur);
  });
  return segs;
}

function assignmentLabel(seg, wsById) {
  if (seg.state === "break") return "Meal break";
  if (seg.state === "work") return seg.ws ? (wsById[seg.ws]?.name || "Work") : "Backoffice / Floating";
  return "Off";
}

function rows(schedule, config, agents, workstations, professions) {
  const agentById = Object.fromEntries(agents.map((a) => [a.id, a]));
  const profById = Object.fromEntries(professions.map((p) => [p.id, p.name]));
  const wsById = Object.fromEntries(workstations.map((w) => [w.id, w]));
  const dayMeta = config.days;
  return buildSegments(schedule, config)
    .filter((s) => s.state === "work" || s.state === "break")
    .map((s) => {
      const a = agentById[s.agentId] || {};
      const start = slotToLabel(config, s.startSlot);
      const end = slotToLabel(config, s.endSlot);
      return {
        day: s.day,
        date: dayMeta[s.day]?.date || "",
        dayLabel: dayMeta[s.day]?.label || "",
        agent: a.name || s.agentId,
        profession: profById[a.profession_id] || "",
        start, end,
        hours: shiftHours(start, end === "24:00" ? "00:00" : end).toFixed(2),
        assignment: assignmentLabel(s, wsById),
      };
    })
    .sort((x, y) => x.day - y.day || x.agent.localeCompare(y.agent) || x.start.localeCompare(y.start));
}

function download(filename, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export function exportCSV(schedule, config, agents, workstations, professions) {
  const data = rows(schedule, config, agents, workstations, professions);
  const header = ["Date", "Day", "Agent", "Profession", "Start", "End", "Hours", "Assignment"];
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [header.join(",")];
  data.forEach((r) => lines.push([r.date, r.dayLabel, r.agent, r.profession, r.start, r.end, r.hours, r.assignment].map(esc).join(",")));
  const week = `${config.days[0].date}_${config.days[config.n_days - 1].date}`;
  download(`schedule_${week}.csv`, lines.join("\n"), "text/csv;charset=utf-8;");
  return data.length;
}

export function exportPDF(schedule, config, agents, workstations, professions) {
  const data = rows(schedule, config, agents, workstations, professions);
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const week = `${config.days[0].date} → ${config.days[config.n_days - 1].date}`;

  config.days.forEach((dm, di) => {
    if (di > 0) doc.addPage();
    doc.setFontSize(15); doc.setTextColor(20);
    doc.text("Master Schedule", 40, 42);
    doc.setFontSize(10); doc.setTextColor(110);
    doc.text(`Week ${week}`, 40, 58);
    doc.setFontSize(13); doc.setTextColor(20);
    doc.text(`${dm.label} — ${dm.date}`, 40, 84);

    const dayRows = data.filter((r) => r.day === di).map((r) => [r.agent, r.profession, r.start, r.end, r.hours, r.assignment]);
    autoTable(doc, {
      startY: 96,
      head: [["Agent", "Profession", "Start", "End", "Hrs", "Assignment"]],
      body: dayRows.length ? dayRows : [["—", "No shifts scheduled", "", "", "", ""]],
      styles: { fontSize: 8.5, cellPadding: 3 },
      headStyles: { fillColor: [10, 132, 255], textColor: 255 },
      alternateRowStyles: { fillColor: [244, 247, 251] },
      columnStyles: { 2: { halign: "center" }, 3: { halign: "center" }, 4: { halign: "center" } },
      margin: { left: 40, right: 40 },
    });
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`Page ${i} / ${pages}`, doc.internal.pageSize.getWidth() - 80, doc.internal.pageSize.getHeight() - 20);
  }
  doc.save(`schedule_${config.days[0].date}_${config.days[config.n_days - 1].date}.pdf`);
  return data.length;
}
